import { z } from "zod";

export type PromptField = "prompt" | "text" | "title" | "synopsis";
export type ReferenceKind = "image" | "video" | "audio";
export type PromptSegment =
  | { type: "text"; text: string }
  | { type: "reference"; slotId: string; kind: ReferenceKind; label: string };
export interface PromptContent { segments: PromptSegment[] }
export interface PromptSource { nodeId: string; field: PromptField; inputEdgeId: string | null }
export interface BindingTarget { edgeId: string; nodeId: string; assetId: string; kind: ReferenceKind }
export type MissingReason = "unbound" | "edge_removed" | "node_removed" | "asset_replaced" | "legacy_unresolved" | "not_copied";
export interface PromptBinding {
  source: PromptSource;
  slotId: string;
  value: { state: "bound"; target: BindingTarget }
    | { state: "missing"; reason: MissingReason; previous?: BindingTarget };
}

export const referenceKindSchema = z.enum(["image", "video", "audio"]);
export const promptSlotIdSchema = z.string().regex(/^[A-Za-z0-9_-]{1,80}$/u);
export const promptSegmentSchema = z.discriminatedUnion("type", [
  z.strictObject({ type: z.literal("text"), text: z.string() }),
  z.strictObject({ type: z.literal("reference"), slotId: promptSlotIdSchema, kind: referenceKindSchema, label: z.string().min(1).max(255) })
]);
export const promptContentSchema = z.strictObject({ segments: z.array(promptSegmentSchema).max(10_000) })
  .superRefine((prompt, context) => {
    const definitions = new Map<string, Extract<PromptSegment, { type: "reference" }>>();
    prompt.segments.forEach((segment, index) => {
      if (segment.type !== "reference") return;
      const previous = definitions.get(segment.slotId);
      if (previous && (previous.kind !== segment.kind || previous.label !== segment.label)) {
        context.addIssue({ code: "custom", path: ["segments", index], message: "相同引用槽位的类型和标签必须一致。" });
      }
      definitions.set(segment.slotId, segment);
    });
  });
export const promptSourceSchema = z.strictObject({
  nodeId: z.uuid(), field: z.enum(["prompt", "text", "title", "synopsis"]), inputEdgeId: z.uuid().nullable()
});
export const bindingTargetSchema = z.strictObject({ edgeId: z.uuid(), nodeId: z.uuid(), assetId: z.uuid(), kind: referenceKindSchema });
export const promptBindingSchema = z.strictObject({
  source: promptSourceSchema,
  slotId: promptSlotIdSchema,
  value: z.discriminatedUnion("state", [
    z.strictObject({ state: z.literal("bound"), target: bindingTargetSchema }),
    z.strictObject({ state: z.literal("missing"), reason: z.enum(["unbound", "edge_removed", "node_removed", "asset_replaced", "legacy_unresolved", "not_copied"]), previous: bindingTargetSchema.optional() })
  ])
}).transform((binding): PromptBinding => {
  if (binding.value.state === "bound") return { ...binding, value: binding.value };
  const { previous, ...value } = binding.value;
  return { ...binding, value: previous === undefined ? value : { ...value, previous } };
});

const kindLabels: Record<ReferenceKind, string> = { image: "图片", video: "视频", audio: "音频" };
const labelKinds: Record<string, ReferenceKind> = { 图片: "image", 视频: "video", 音频: "audio" };
const reserved = /[\\{}#:]/gu;
const escaped = /\\([\\{}#:])/gu;
const escapeText = (text: string): string => text.replace(reserved, "\\$&");

export function plainPrompt(text: string): PromptContent {
  return { segments: text.length ? [{ type: "text", text }] : [] };
}

/** Semantic labels only: this is neither portable syntax nor a model mention. */
export function promptToPlainText(prompt: PromptContent): string {
  return prompt.segments.map((segment) => segment.type === "text" ? segment.text : segment.label).join("");
}

export function serializePortablePrompt(prompt: PromptContent): string {
  promptContentSchema.parse(prompt);
  const numbers = new Map<string, number>();
  return prompt.segments.map((segment) => {
    if (segment.type === "text") return escapeText(segment.text);
    if (!numbers.has(segment.slotId)) numbers.set(segment.slotId, numbers.size + 1);
    return `{{${kindLabels[segment.kind]}#${numbers.get(segment.slotId)}:${escapeText(segment.label)}}}`;
  }).join("");
}

/** Template-local numbers never select a media input. This function creates no bindings. */
export function parsePortablePrompt(text: string, nextSlotId: () => string): PromptContent {
  const segments: PromptSegment[] = [];
  const definitions = new Map<string, Extract<PromptSegment, { type: "reference" }>>();
  const allocated = new Set<string>();
  const allocate = (): string => {
    const id = promptSlotIdSchema.parse(nextSlotId());
    if (allocated.has(id)) throw new Error("新引用槽位 ID 必须唯一。");
    allocated.add(id);
    return id;
  };
  const appendText = (value: string): void => {
    if (!value) return;
    const last = segments.at(-1);
    if (last?.type === "text") last.text += value;
    else segments.push({ type: "text", text: value });
  };
  let index = 0;
  while (index < text.length) {
    if (text[index] === "\\" && /[\\{}#:]/u.test(text[index + 1] ?? "")) {
      appendText(text[index + 1]!); index += 2; continue;
    }
    if (!text.startsWith("{{", index)) { appendText(text[index]!); index++; continue; }
    let end = index + 2;
    while (end < text.length && !text.startsWith("}}", end)) {
      end += text[end] === "\\" && end + 1 < text.length ? 2 : 1;
    }
    end = Math.min(text.length, end + 2);
    const raw = text.slice(index, end);
    const prefix = /^\{\{(图片|视频|音频)/u.exec(raw);
    const match = /^\{\{(图片|视频|音频)#([1-9]\d*):((?:\\[\\{}#:]|[^\\{}#:])*)\}\}$/u.exec(raw);
    const kind = prefix ? labelKinds[prefix[1]!]! : undefined;
    const label = match?.[3]?.replace(escaped, "$1");
    const previous = match ? definitions.get(match[2]!) : undefined;
    if (match && kind && label && label.length <= 255 && (!previous || (previous.kind === kind && previous.label === label))) {
      const segment = previous ?? { type: "reference" as const, slotId: allocate(), kind, label };
      definitions.set(match[2]!, segment);
      segments.push({ ...segment });
    } else {
      // Preserve malformed raw input losslessly, plus a persistable unresolved slot.
      appendText(raw);
      if (kind) segments.push({ type: "reference", slotId: allocate(), kind, label: `${kindLabels[kind]}参考待确认` });
    }
    index = end;
  }
  return promptContentSchema.parse({ segments });
}
